import { Component } from '@theme/component';

/**
 * Tabs component for the Featured Collection section.
 *
 * Each `_featured-collection-tab` block renders its own pane (product grid)
 * with `data-tab-block` and `data-tab-pane` markers. This component:
 *  1. Discovers all rendered tab blocks inside itself.
 *  2. Creates trigger buttons from `data-tab-label` attributes.
 *  3. Moves the nav into the section header for inline layout.
 *  4. Handles tab switching and keyboard navigation (WAI-ARIA Tabs pattern).
 *  5. When data-tab-style="underline", places a "Ver mais" link in the header
 *     and updates it to the active tab's collection link on each switch.
 *  6. Injects prev/next controls for any carousel-layout grids.
 *
 * @extends {Component}
 */
class FeaturedCollectionTabs extends Component {
  /** @type {HTMLButtonElement[]} */
  #triggerButtons = [];

  /** @type {HTMLElement[]} */
  #tabPanes = [];

  /** @type {HTMLAnchorElement | null} */
  #viewAllLink = null;


  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  #init() {
    const tabBlocks = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-tab-block]')]);
    if (tabBlocks.length === 0) return;

    const nav = this.#buildNav(tabBlocks);
    this.#insertNav(nav);
    this.#hideInactivePanes();

    for (const pane of this.#tabPanes) {
      this.#initCarousel(pane);
    }

    this.classList.add('featured-collection-tabs--has-tabs');

    const firstBtn = this.#triggerButtons[0];
    if (firstBtn) this.#updateViewAll(firstBtn);
  }

  /**
   * Build the tab navigation from rendered tab blocks.
   *
   * @param {HTMLElement[]} tabBlocks
   * @returns {HTMLElement}
   */
  #buildNav(tabBlocks) {
    const nav = document.createElement('div');
    nav.className = 'featured-collection-tabs__nav';
    nav.setAttribute('role', 'tablist');

    for (const [i, block] of tabBlocks.entries()) {
      const label = block.dataset.tabLabel || 'Tab';
      const blockId = block.dataset.tabId || block.id;
      const pane = block.querySelector('[data-tab-pane]');
      const iconTpl = /** @type {HTMLTemplateElement | null} */ (
        block.querySelector('template[data-tab-icon]')
      );

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'tab');
      btn.id = `tab-trigger-${blockId}`;
      btn.setAttribute('aria-selected', String(i === 0));
      btn.tabIndex = i === 0 ? 0 : -1;
      btn.className = 'featured-collection-tabs__trigger';

      btn.dataset.linkUrl = block.dataset.tabLinkUrl || '';
      btn.dataset.linkText = block.dataset.tabLinkText || '';
      btn.dataset.linkFontWeight = block.dataset.tabLinkFontWeight || '';

      // Per-tab label typography (from the tab block's settings). Applied as CSS
      // vars the trigger CSS reads with a fallback, so unset tabs are unchanged.
      const fontSize = block.dataset.tabFontSize;
      if (fontSize) btn.style.setProperty('--tabs-trigger-font-size', `${fontSize}px`);
      // Inline on the trigger, so a per-tab mobile size wins over the section-wide
      // one inherited from .section-resource-list — same "more specific wins"
      // cascade the desktop size already has.
      const fontSizeMobile = block.dataset.tabFontSizeMobile;
      if (fontSizeMobile) btn.style.setProperty('--tabs-trigger-font-size-mobile', `${fontSizeMobile}px`);
      const fontWeight = block.dataset.tabFontWeight;
      if (fontWeight) btn.style.setProperty('--tabs-trigger-font-weight', fontWeight);

      if (pane) {
        btn.setAttribute('aria-controls', pane.id);
        pane.setAttribute('role', 'tabpanel');
        pane.setAttribute('aria-labelledby', btn.id);
        this.#tabPanes.push(/** @type {HTMLElement} */ (pane));
      }

      if (iconTpl) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'featured-collection-tabs__icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        iconWrap.appendChild(iconTpl.content.cloneNode(true));
        btn.appendChild(iconWrap);
        btn.classList.add('featured-collection-tabs__trigger--with-icon');
      }

      const labelSpan = document.createElement('span');
      labelSpan.className = 'featured-collection-tabs__label';
      labelSpan.textContent = label;
      btn.appendChild(labelSpan);

      nav.appendChild(btn);
      this.#triggerButtons.push(btn);
    }

    nav.addEventListener('click', (e) => {
      const btn = /** @type {Element | null} */ (e.target)?.closest('button[role="tab"]');
      if (btn instanceof HTMLButtonElement) this.#activate(btn);
    });

    nav.addEventListener('keydown', (e) => {
      if (e instanceof KeyboardEvent) this.#onKeydown(e);
    });

    return nav;
  }

  /**
   * Insert the nav into the section header for inline layout.
   * In underline mode, also inserts a "Ver mais" link slot on the right
   * of the header (same row as the title). The nav wraps to its own row
   * via CSS (flex: 0 0 100% + flex-wrap: wrap on the header).
   * The centered alignment gets the same link slot; there the header becomes a
   * three-column grid and CSS `order` puts the nav between title and link, so
   * the DOM order stays the same for both.
   * Falls back to prepending inside the component itself.
   *
   * @param {HTMLElement} nav
   */
  #insertNav(nav) {
    const colorScheme = this.dataset.tabsColorScheme;
    if (colorScheme) nav.classList.add(colorScheme);

    const inactiveBg = this.style.getPropertyValue('--tabs-trigger-inactive-bg').trim();
    if (inactiveBg) nav.style.setProperty('--tabs-trigger-inactive-bg', inactiveBg);

    const inactiveColor = this.style.getPropertyValue('--tabs-trigger-inactive-color').trim();
    if (inactiveColor) nav.style.setProperty('--tabs-trigger-inactive-color', inactiveColor);

    const section = this.closest('.section-resource-list');
    const header = section?.querySelector('.section-resource-list__header');

    if (header) {
      header.classList.add('featured-collection-tabs__header-row');

      const titleWrap = document.createElement('div');
      titleWrap.className = 'featured-collection-tabs__header-title';
      while (header.firstChild) titleWrap.appendChild(header.firstChild);

      header.appendChild(titleWrap);

      if (this.dataset.tabStyle === 'underline' || this.dataset.tabsAlignment === 'center') {
        const viewAllLink = document.createElement('a');
        viewAllLink.className = 'featured-collection-tabs__view-all-link';
        // Opt-in button styling (section setting `tabs_view_all_style`). The value is
        // whitelisted rather than concatenated, so nothing but a theme button class can
        // ever land here; unset = the plain text link every store renders today.
        const viewAllStyle = this.dataset.tabsViewAllStyle;
        if (viewAllStyle === 'button' || viewAllStyle === 'button-secondary') {
          viewAllLink.classList.add(viewAllStyle, 'featured-collection-tabs__view-all-link--button');
        }
        viewAllLink.hidden = true;
        header.appendChild(viewAllLink);
        this.#viewAllLink = viewAllLink;
      }

      header.appendChild(nav);
    } else {
      this.prepend(nav);
    }
  }

  #hideInactivePanes() {
    for (const [i, pane] of this.#tabPanes.entries()) {
      if (i > 0) pane.hidden = true;
      else pane.removeAttribute('hidden');
    }
  }

  /**
   * Inject carousel controls below (or over) a carousel-layout grid, matching
   * the per-tab `carousel_controls` style: 'arrows' (side overlay), 'dots', or
   * 'progressbar'. Does nothing if the pane has no carousel grid.
   *
   * 'arrows' and 'progressbar' delegate scroll/fill/disabled behaviour to the
   * shared <carousel-progress> element (assets/carousel-progress.js) so the
   * visual matches the native product carousel. 'dots' is handled locally.
   *
   * @param {HTMLElement} pane
   */
  #initCarousel(pane) {
    const grid = pane.querySelector('.featured-collection-tabs__grid--carousel');
    if (!(grid instanceof HTMLElement)) return;

    const tabBlock = pane.closest('[data-tab-block]');
    const style =
      (tabBlock instanceof HTMLElement && tabBlock.dataset.carouselControls) || 'progressbar';

    if (style === 'dots') {
      this.#initDots(pane, grid);
      return;
    }

    // 'arrows' | 'progressbar' — shared <carousel-progress> resolves the grid
    // (scoped to this pane) and wires the buttons + optional fill.
    const controls = document.createElement('carousel-progress');
    controls.className = `carousel-controls carousel-controls--${style}`;
    controls.dataset.scope = '[data-tab-pane]';
    controls.dataset.scroller = '.featured-collection-tabs__grid--carousel';

    if (style === 'progressbar') {
      const progress = document.createElement('div');
      progress.className = 'carousel-controls__progress';
      const fill = document.createElement('span');
      fill.className = 'carousel-controls__progress-fill';
      progress.appendChild(fill);
      controls.appendChild(progress);
    }

    controls.appendChild(this.#navButton('prev'));
    controls.appendChild(this.#navButton('next'));
    pane.appendChild(controls);
  }

  /**
   * Build a round prev/next control button wired for <carousel-progress>.
   *
   * @param {'prev' | 'next'} direction
   * @returns {HTMLButtonElement}
   */
  #navButton(direction) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'carousel-controls__btn';
    btn.setAttribute('aria-label', direction === 'prev' ? 'Anterior' : 'Próximo');
    if (direction === 'prev') btn.setAttribute('data-carousel-prev', '');
    else btn.setAttribute('data-carousel-next', '');
    const points = direction === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    btn.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><polyline points="${points}"/></svg>`;
    return btn;
  }

  /**
   * Render dot controls (one per item) below a carousel grid and keep the
   * active dot in sync with the scroll position.
   *
   * @param {HTMLElement} pane
   * @param {HTMLElement} grid
   */
  #initDots(pane, grid) {
    const items = /** @type {HTMLElement[]} */ ([...grid.children]);
    if (items.length === 0) return;

    const dots = document.createElement('ol');
    dots.className = 'carousel-controls carousel-controls--dots carousel-controls__dots';

    const buttons = items.map((item, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'carousel-controls__dot';
      btn.setAttribute('aria-label', String(i + 1));
      if (i === 0) btn.setAttribute('aria-selected', 'true');
      btn.addEventListener('click', () => {
        const left = item.getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft;
        grid.scrollTo({ left, behavior: 'smooth' });
      });
      li.appendChild(btn);
      dots.appendChild(li);
      return btn;
    });

    pane.appendChild(dots);

    const update = () => {
      let active = 0;
      let min = Infinity;
      const gridLeft = grid.getBoundingClientRect().left;
      items.forEach((item, i) => {
        const distance = Math.abs(item.getBoundingClientRect().left - gridLeft);
        if (distance < min) {
          min = distance;
          active = i;
        }
      });
      buttons.forEach((btn, i) => btn.setAttribute('aria-selected', String(i === active)));
    };

    grid.addEventListener('scroll', update, { passive: true });

    // ResizeObserver fires when the grid becomes visible after a tab switch,
    // guaranteeing correct geometry at that point.
    new ResizeObserver(update).observe(grid);
  }

  /**
   * Update the "Ver mais" link href and text based on the active trigger.
   *
   * @param {HTMLButtonElement} trigger
   */
  #updateViewAll(trigger) {
    if (!this.#viewAllLink) return;
    const url = trigger.dataset.linkUrl;
    const text = trigger.dataset.linkText;

    // Per-tab weight: one link element is shared by every tab, so it has to be
    // re-applied (or cleared) on each switch. Cleared = the CSS fallback, i.e.
    // the inherited weight the link has always had.
    const weight = trigger.dataset.linkFontWeight;
    if (weight) this.#viewAllLink.style.setProperty('--tabs-view-all-weight', weight);
    else this.#viewAllLink.style.removeProperty('--tabs-view-all-weight');

    if (url) {
      this.#viewAllLink.href = url;
      this.#viewAllLink.textContent = text || 'Ver mais';
      this.#viewAllLink.hidden = false;
    } else {
      this.#viewAllLink.hidden = true;
    }
  }

  /**
   * @param {HTMLButtonElement} trigger
   */
  #activate(trigger) {
    const targetId = trigger.getAttribute('aria-controls');
    if (!targetId) return;

    for (const btn of this.#triggerButtons) {
      const active = btn === trigger;
      btn.setAttribute('aria-selected', String(active));
      btn.tabIndex = active ? 0 : -1;
    }

    for (const pane of this.#tabPanes) {
      pane.hidden = pane.id !== targetId;
    }

    this.#updateViewAll(trigger);
  }

  /**
   * Roving tabindex keyboard navigation per WAI-ARIA tabs pattern.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    const current = /** @type {Element | null} */ (event.target)?.closest('button[role="tab"]');
    if (!(current instanceof HTMLButtonElement)) return;

    const idx = this.#triggerButtons.indexOf(current);
    if (idx === -1) return;

    let next = idx;
    switch (event.key) {
      case 'ArrowRight':
        next = (idx + 1) % this.#triggerButtons.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + this.#triggerButtons.length) % this.#triggerButtons.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.#triggerButtons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextBtn = this.#triggerButtons[next];
    if (nextBtn) {
      this.#activate(nextBtn);
      nextBtn.focus();
    }
  }
}

if (!customElements.get('featured-collection-tabs')) {
  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
